import React, { useState, useEffect, useMemo } from 'react';
import { FormGroup } from 'react-bootstrap';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  getAllBrands,
  getCategories,
  getProductTypes,
  getMultiSubBrandsByBrandIds,
  getCatBasedOnPT,
} from '../../../../services/system-canvas/system-canvas.service';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';
import type { OptionType } from '../../../../Components/Controls/bx-select.types';
import { ClearIcon, FilterIcon, TickIcon } from '../../../../bombiix-icon';
import { NewSelectMultiSelect } from '../../../../Components/PIM/common';

interface SystemCanvasModulesFilterProps {
  onApplyFilter: (filters: FilterState) => void;
  filterActive: boolean;
}

interface FilterState {
  brandId: string[];
  subBrandId: string[];
  productType: string[];
  catOneId: string[];
  catTwoId: string[];
}

const SystemCanvasModulesFilter: React.FC<SystemCanvasModulesFilterProps> = ({ onApplyFilter, filterActive }) => {
  const { t } = useTranslation(['common', 'custom-module-system-canvas']);

  const [brandsOptions, setBrandsOptions] = useState<OptionType[]>([]);
  const [subBrandsOptions, setSubBrandsOptions] = useState<OptionType[]>([]);
  const [productTypeOptions, setProductTypeOptions] = useState<OptionType[]>([]);
  const [categoryType1Options, setCategoryType1Options] = useState<OptionType[]>([]);
  const [categoryType2Options, setCategoryType2Options] = useState<OptionType[]>([]);
  const [allCategories1, setAllCategories1] = useState<any[]>([]);
  const [allCategories2, setAllCategories2] = useState<any[]>([]);
  const [catBasedOnPT, setCatBasedOnPT] = useState<boolean>(false);

  const [filterState, setFilterState] = useState<FilterState>({
    brandId: [],
    subBrandId: [],
    productType: [],
    catOneId: [],
    catTwoId: [],
  });

  const reducer = useSelector((reducerState: any) => reducerState);
  const { UserDetails } = reducer;
  const { userInfo } = UserDetails || {};

  useEffect(() => {
    getCatBasedOnPTData();
    getBrandsList();
    getProductTypeList();
    getCategoriesList({ categoryType: 1 });
    getCategoriesList({ categoryType: 2 });
  }, []);

  useEffect(() => {
    setSubBrandsOptions([]);
    if (filterState.brandId.length > 0) {
      getSubBrandsList();
    }
  }, [filterState.brandId]);

  useEffect(() => {
    if (catBasedOnPT && allCategories1.length > 0) {
      applyCategoryFiltering(1);
    }
  }, [filterState.productType, catBasedOnPT, allCategories1]);

  useEffect(() => {
    if (catBasedOnPT && allCategories2.length > 0) {
      applyCategoryFiltering(2);
    }
  }, [filterState.catOneId, catBasedOnPT, allCategories2]);

  const getCatBasedOnPTData = async () => {
    try {
      const catBasedOnPTFlag = await getCatBasedOnPT();
      if (catBasedOnPTFlag?.status) {
        setCatBasedOnPT(!!catBasedOnPTFlag.data);
      } else {
        setCatBasedOnPT(false);
      }
    } catch (error) {
      setCatBasedOnPT(false);
    }
  };

  const getBrandsList = async () => {
    try {
      const brands = await getAllBrands({ loggedInUserId: userInfo?.userId, isActive: true });
      if (brands?.status) {
        setBrandsOptions(
          brands.data.map((x: any) => ({
            label: x.name,
            value: x._id.toString(),
            _id: x._id.toString(),
          })),
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getSubBrandsList = async () => {
    try {
      if (filterState.brandId.length === 0) {
        setSubBrandsOptions([]);
        return;
      }
      const subBrands = await getMultiSubBrandsByBrandIds({ brandIds: filterState.brandId, isActive: true });
      if (subBrands?.status) {
        const options = subBrands.data.map((subBrand) => ({
          label: subBrand.name,
          value: subBrand._id.toString(),
          _id: subBrand._id.toString(),
        }));
        const availableSubBrandIds = options.map((option) => option.value);
        setSubBrandsOptions(options);
        setFilterState((prevState) => ({
          ...prevState,
          subBrandId: prevState.subBrandId.filter((subBrandId) => availableSubBrandIds.includes(subBrandId)),
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getProductTypeList = async () => {
    try {
      const productTypes = await getProductTypes({ loggedInUserId: userInfo?.userId, isActive: true, tier: 'Tier1' });
      if (productTypes?.status) {
        setProductTypeOptions(
          productTypes.data.map((x: any) => ({
            label: x.name,
            value: x._id.toString(),
            _id: x._id.toString(),
          })),
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getCategoriesList = async ({ categoryType }: { categoryType: number }) => {
    try {
      const categories = await getCategories({ categoryType, loggedInUserId: userInfo?.userId, isActive: true });
      if (categories?.status) {
        if (categoryType === 1) {
          setAllCategories1(categories.data);
          setCategoryType1Options(
            categories.data.map((x: any) => ({
              label: x.name,
              value: x._id.toString(),
              _id: x._id.toString(),
            })),
          );
        } else if (categoryType === 2) {
          setAllCategories2(categories.data);
          setCategoryType2Options(
            categories.data.map((x: any) => ({
              label: x.name,
              value: x._id.toString(),
              _id: x._id.toString(),
            })),
          );
        }
        if (catBasedOnPT) {
          applyCategoryFiltering(categoryType);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const applyCategoryFiltering = (categoryType: number) => {
    if (categoryType === 1) {
      let filteredData = allCategories1;

      if (catBasedOnPT && filterState.productType.length > 0) {
        filteredData = allCategories1.filter(
          (cat: any) => cat.productType && Array.isArray(cat.productType) && cat.productType.some((pt: any) => filterState.productType.includes(pt._id || pt)),
        );
      }
      setCategoryType1Options(
        filteredData.map((x: any) => ({
          label: x.name,
          value: x._id.toString(),
          _id: x._id.toString(),
        })),
      );
    } else if (categoryType === 2) {
      let filteredData = allCategories2;

      if (catBasedOnPT && filterState.catOneId.length > 0) {
        filteredData = allCategories2.filter(
          (cat: any) => cat.catOneIds && Array.isArray(cat.catOneIds) && cat.catOneIds.some((c1: any) => filterState.catOneId.includes(c1._id || c1)),
        );
      }

      setCategoryType2Options(
        filteredData.map((x: any) => ({
          label: x.name,
          value: x._id.toString(),
          _id: x._id.toString(),
        })),
      );
    }
  };

  const handleFilterSelect = (val: OptionType[], key: keyof FilterState) => {
    const valueIds = val.map((valueItem) => valueItem.value);

    if (key === 'brandId') {
      setFilterState((prevState) => ({
        ...prevState,
        brandId: valueIds,
        subBrandId: valueIds.length > 0 ? prevState.subBrandId : [],
      }));
    } else if (key === 'productType' && catBasedOnPT) {
      setFilterState((prevState) => ({
        ...prevState,
        productType: valueIds,
        catOneId: [],
        catTwoId: [],
      }));
    } else if (key === 'catOneId' && catBasedOnPT) {
      setFilterState((prevState) => ({
        ...prevState,
        catOneId: valueIds,
        catTwoId: [],
      }));
    } else {
      setFilterState((prevState) => ({
        ...prevState,
        [key]: valueIds,
      }));
    }
  };

  const handleApplyFilter = () => {
    onApplyFilter(filterState);
  };

  const handleClearFilter = () => {
    const clearedState: FilterState = {
      brandId: [],
      subBrandId: [],
      productType: [],
      catOneId: [],
      catTwoId: [],
    };
    setFilterState(clearedState);
    onApplyFilter(clearedState);
  };

  const brandMultiSelectOptions = useMemo(() => brandsOptions.map((opt) => ({ label: opt.label, value: opt.value })), [brandsOptions]);
  const subBrandMultiSelectOptions = useMemo(() => subBrandsOptions.map((opt) => ({ label: opt.label, value: opt.value })), [subBrandsOptions]);
  const productTypeMultiSelectOptions = useMemo(() => productTypeOptions.map((opt) => ({ label: opt.label, value: opt.value })), [productTypeOptions]);
  const cat1MultiSelectOptions = useMemo(() => categoryType1Options.map((opt) => ({ label: opt.label, value: opt.value })), [categoryType1Options]);
  const cat2MultiSelectOptions = useMemo(() => categoryType2Options.map((opt) => ({ label: opt.label, value: opt.value })), [categoryType2Options]);

  const hasSelectedFilters = useMemo(() => {
    return (
      filterState.brandId.length > 0 ||
      filterState.subBrandId.length > 0 ||
      filterState.productType.length > 0 ||
      filterState.catOneId.length > 0 ||
      filterState.catTwoId.length > 0
    );
  }, [filterState]);

  const showActiveIndicator = filterActive || hasSelectedFilters;

  const filterFields: Array<{ key: keyof FilterState; options: OptionType[]; placeholderKey: string }> = [
    { key: 'brandId', options: brandMultiSelectOptions, placeholderKey: 'CUSTOM_MODULE.SYSTEM_CANVAS.SELECT_BRAND' },
    { key: 'subBrandId', options: subBrandMultiSelectOptions, placeholderKey: 'CUSTOM_MODULE.SYSTEM_CANVAS.SELECT_SUB_BRAND' },
    { key: 'productType', options: productTypeMultiSelectOptions, placeholderKey: 'CUSTOM_MODULE.SYSTEM_CANVAS.SELECT_PRODUCT_TYPE' },
    { key: 'catOneId', options: cat1MultiSelectOptions, placeholderKey: 'CUSTOM_MODULE.SYSTEM_CANVAS.SELECT_CATEGORY_1' },
    { key: 'catTwoId', options: cat2MultiSelectOptions, placeholderKey: 'CUSTOM_MODULE.SYSTEM_CANVAS.SELECT_CATEGORY_2' },
  ];

  return (
    <CustomTooltip placement="top" tooltipText={t('FILTER_BUTTON.FILTER')}>
      <OverlayTrigger
        placement="bottom-end"
        trigger="click"
        rootClose
        overlay={
          <Popover id="system-canvas-modules-filter-popover" className="custom-popover cm-filter-popover">
            <Popover.Content>
              <div className="row">
                {filterFields.map((field) => (
                  <div className="col-md-6" key={field.key}>
                    <FormGroup>
                      <NewSelectMultiSelect
                        isMulti
                        name={field.key}
                        options={field.options}
                        containerClassName="w-100"
                        value={field.options.filter((option) => filterState[field.key].includes(option.value))}
                        placeholder={t(field.placeholderKey, { ns: 'custom-module-system-canvas' })}
                        onChange={(selected) => handleFilterSelect((selected ?? []) as OptionType[], field.key)}
                        selectAllLabel={t('COMMON_LABEL.SELECT_ALL')}
                      />
                    </FormGroup>
                  </div>
                ))}
              </div>
              <div className="d-flex justify-content-end">
                <CustomTooltip placement="top" tooltipText={t('FILTER_BUTTON.CLEAR')}>
                  <button
                    onClick={handleClearFilter}
                    aria-label={t('FILTER_BUTTON.CLEAR')}
                    className="bombiix-icon-container bombiix-icon-container--red"
                    type="button"
                  >
                    <ClearIcon size={24} />
                  </button>
                </CustomTooltip>
                <CustomTooltip placement="top" tooltipText={t('FILTER_BUTTON.APPLY')}>
                  <button onClick={handleApplyFilter} aria-label={t('FILTER_BUTTON.APPLY')} className="bombiix-icon-container ml-16" type="button">
                    <TickIcon size={24} />
                  </button>
                </CustomTooltip>
              </div>
            </Popover.Content>
          </Popover>
        }
      >
        <span className="filter-icon-wrapper ml-1 cursor-pointer">
          <FilterIcon size={24} />
          {showActiveIndicator && <span className="filter-active-dot" />}
        </span>
      </OverlayTrigger>
    </CustomTooltip>
  );
};

export default SystemCanvasModulesFilter;
